import React from 'react';
import {useCodeLikeInput} from '../useCodeLikeInput';
import styles from './PasswordInput.module.css';

const CODE_LENGTH = 4;
const SHAKE_DURATION_MS = 820;
const GLOW_BLACK = 'rgba(0, 0, 0, 0.45)'; // black glow for password dots
const GLOW_RED = 'rgba(253, 146, 124, 0.55)'; // close to bg-[#FD927C]
const STAGGER_DELAY_MS = 45;
const LOADING_STEP_MS = 420;

interface PasswordInputProps {
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	isLoading?: boolean;
	errorNonce?: number;
	/** Hides the system keyboard (use with an on-screen keypad on narrow viewports). */
	suppressNativeKeyboard?: boolean;
}

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
	{value, onChange, disabled, isLoading, errorNonce, suppressNativeKeyboard},
	ref,
) {
	const {digits, isErrorAnimating, setInputRef, getBubbleRef, handleMouseDown, handleBlur, handleNativeChange} =
		useCodeLikeInput({
			value,
			onChange,
			disabled,
			errorNonce,
			codeLength: CODE_LENGTH,
			shakeDurationMs: SHAKE_DURATION_MS,
			staggerDelayMs: STAGGER_DELAY_MS,
			glowDefault: GLOW_BLACK,
			glowError: GLOW_RED,
			forwardedRef: ref,
		});

	const [loadingIndex, setLoadingIndex] = React.useState(0);

	React.useEffect(() => {
		if (!isLoading) return;
		setLoadingIndex(0);

		const interval = window.setInterval(() => {
			setLoadingIndex((idx) => (idx + 1) % CODE_LENGTH);
		}, LOADING_STEP_MS);

		return () => window.clearInterval(interval);
	}, [isLoading]);

	return (
		<div
			className={`${styles.root} ${isErrorAnimating ? 'shake' : ''}`}
			style={{'--loading-step-ms': `${LOADING_STEP_MS}ms`} as React.CSSProperties}
		>
			<div className={styles.digitsRow} onMouseDown={handleMouseDown}>
				{Array.from({length: CODE_LENGTH}).map((_, idx) => {
					const char = digits[idx] ?? '';
					const isActiveLoading = Boolean(isLoading) && idx === loadingIndex;

					return (
						<div
							key={idx}
							className={`${styles.cell} ${char ? `${styles.cellBorderFilled} ${styles.cellWithDigit}` : styles.cellBorderEmpty} ${isActiveLoading ? styles.cellLoading : ''}`}
							aria-hidden="true"
						>
							{char ? (
								<span ref={getBubbleRef(idx)} className={styles.bubble} />
							) : isActiveLoading ? (
								<span className={styles.bubbleLoading} />
							) : null}
						</div>
					);
				})}
			</div>
			<input
				type="text"
				name="pin"
				value={digits}
				onChange={suppressNativeKeyboard ? undefined : handleNativeChange}
				onBlur={handleBlur}
				className={styles.hiddenInput}
				disabled={disabled}
				readOnly={suppressNativeKeyboard}
				minLength={CODE_LENGTH}
				maxLength={CODE_LENGTH}
				inputMode={suppressNativeKeyboard ? 'none' : 'numeric'}
				pattern="[0-9]*"
				autoFocus
				autoComplete="off"
				autoCorrect="off"
				autoCapitalize="off"
				spellCheck={false}
				ref={setInputRef}
			/>
		</div>
	);
});
